import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LobbyStatePayload, BotsMatchOfferPayload } from '@sheshdash/shared';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { GameModeModal } from './GameModeModal';

interface LobbyScreenProps {
  playerName: string;
  setPlayerName: (name: string) => void;
  preferredColor: string;
  setPreferredColor: (color: string) => void;
  isConnected: boolean;
  isLoggedIn: boolean;
  error: string | null;
  lobbyState: LobbyStatePayload | null;
  botsMatchOffer: BotsMatchOfferPayload | null;
  onCreateGame: (isPrivate: boolean, preferredColor: string) => void;
  onQuickStart: (preferredColor: string) => void;
  onJoinGame: (gameId: string, preferredColor: string) => void;
  onJoinQueue: () => void;
  onLeaveQueue: () => void;
  onAcceptBotsMatch: () => void;
  onDeclineBotsMatch: () => void;
  onShowRules: () => void;
  onShowUserAgreement: () => void;
  onLogout: () => void;
}

const colorOptions = [
  { value: '', labelKey: 'game.autoAssignColor' },
  { value: 'red', labelKey: 'game.colorRed' },
  { value: 'blue', labelKey: 'game.colorBlue' },
  { value: 'green', labelKey: 'game.colorGreen' },
  { value: 'orange', labelKey: 'game.colorOrange' },
] as const;

export function LobbyScreen({
  playerName, setPlayerName, preferredColor, setPreferredColor, isConnected, isLoggedIn, error,
  lobbyState, botsMatchOffer, onCreateGame, onQuickStart, onJoinGame, onJoinQueue,
  onLeaveQueue, onAcceptBotsMatch, onDeclineBotsMatch, onShowRules,
  onShowUserAgreement, onLogout,
}: LobbyScreenProps) {
  const { t } = useTranslation();
  const [showJoinGame, setShowJoinGame] = useState(false);
  const [showModeModal, setShowModeModal] = useState(false);
  const [gameId, setGameId] = useState('');

  return (
    <div className="w-full max-w-md mx-auto mt-12 px-4 flex flex-col items-center gap-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-14 h-14 bg-slate-900 dark:bg-slate-800 rounded-2xl shadow-xl flex items-center justify-center border-2 border-slate-800 dark:border-slate-700">
          <svg className="w-7 h-7 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <div>
          <h1 className="text-3xl font-black text-slate-800 dark:text-slate-100 tracking-tighter leading-none">SHESHDASH</h1>
          <span className="text-[9px] font-black text-indigo-700 dark:text-indigo-300 tracking-widest uppercase">Fast Moves. Smart Strategy.</span>
        </div>
      </div>

      <div
        data-testid="connection-status"
        data-connection={isConnected ? 'connected' : 'connecting'}
        className={`text-sm font-bold ${isConnected ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'}`}
      >
        {isConnected ? `\u25CF ${t('game.connected')}` : `\u25CF ${t('game.connecting')}`}
      </div>

      {error && (
        <div className="w-full bg-rose-50 dark:bg-rose-950 border border-rose-200 text-rose-700 dark:text-rose-300 rounded-xl p-3 text-sm font-bold">
          {error}
        </div>
      )}

      <div className="w-full bg-white dark:bg-slate-900 rounded-[2rem] shadow-sm border border-slate-100 dark:border-slate-800 p-6 flex flex-col gap-4">
        <input
          type="text"
          placeholder={t('game.yourName')}
          value={playerName}
          onChange={(e) => setPlayerName(e.target.value)}
          disabled={!isConnected}
          data-testid="player-name-input"
          className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-bold text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-900 transition-all"
        />

        <div className="flex flex-col gap-2">
          <label htmlFor="preferred-color" className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {t(isLoggedIn ? 'account.preferredColor' : 'game.preferredColor')}
          </label>
          <select
            id="preferred-color"
            data-testid="preferred-color-select"
            value={preferredColor}
            onChange={(e) => setPreferredColor(e.target.value)}
            disabled={!isConnected}
            className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-bold text-slate-800 dark:text-slate-100 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-900 transition-all"
          >
            {colorOptions.map((option) => (
              <option key={option.value || 'auto'} value={option.value}>{t(option.labelKey)}</option>
            ))}
          </select>
        </div>

        {!showJoinGame ? (
          <>
            <button
              data-testid="create-game"
              onClick={() => setShowModeModal(true)}
              disabled={!isConnected || !playerName.trim()}
              className="w-full py-3 bg-gradient-to-r from-[#6366f1] to-[#a855f7] text-white dark:text-slate-900 font-black rounded-xl shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('game.createGame')}
            </button>
            <button
              data-testid="join-game-toggle"
              onClick={() => setShowJoinGame(true)}
              disabled={!isConnected}
              className="w-full py-3 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-black rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-all disabled:opacity-50"
            >
              {t('game.joinGame')}
            </button>
          </>
        ) : (
          <>
            <input
              type="text"
              placeholder={t('game.gameIdPlaceholder')}
              value={gameId}
              onChange={(e) => setGameId(e.target.value)}
              data-testid="game-id-input"
              className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-bold text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-indigo-400 transition-all"
            />
            <button
              data-testid="join-game-submit"
              onClick={() => onJoinGame(gameId, preferredColor)}
              disabled={!isConnected || !playerName.trim() || !gameId.trim()}
              className="w-full py-3 bg-gradient-to-r from-[#6366f1] to-[#a855f7] text-white dark:text-slate-900 font-black rounded-xl shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('game.joinGameButton')}
            </button>
            <button
              data-testid="join-game-back"
              onClick={() => setShowJoinGame(false)}
              className="w-full py-3 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-black rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
            >
              {t('game.back')}
            </button>
          </>
        )}
      </div>

      <div className="w-full flex flex-col gap-3">
        {lobbyState && (
          <div className="text-sm text-slate-600 dark:text-slate-300 font-bold text-center">
            {t('lobby.onlineCount', { count: lobbyState.online_players.length })}
          </div>
        )}

        <button
          data-testid={lobbyState?.in_queue ? 'leave-queue' : 'join-queue'}
          onClick={() => lobbyState?.in_queue ? onLeaveQueue() : onJoinQueue()}
          disabled={!isConnected || (!playerName.trim() && !lobbyState?.in_queue)}
          className={`w-full py-3 font-black rounded-xl shadow-lg transition-all ${
            lobbyState?.in_queue
              ? 'bg-rose-500 text-white dark:text-slate-900 hover:bg-rose-600 dark:hover:bg-rose-400'
              : 'bg-amber-500 text-white dark:text-slate-900 hover:bg-amber-600 dark:hover:bg-amber-400'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {lobbyState?.in_queue ? t('lobby.cancelMatch') : t('lobby.findMatch')}
        </button>

        {lobbyState?.in_queue && (
          <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 rounded-xl p-4 text-center">
            <div className="font-bold text-amber-700 dark:text-amber-300 mb-1">{t('lobby.searching')}</div>
            <div className="text-sm text-amber-600 dark:text-amber-400">{t('lobby.queueCount', { count: lobbyState.queue_count })}</div>
          </div>
        )}

        {lobbyState?.in_queue && botsMatchOffer?.expires_at && (
          <div data-testid="bots-match-offer" className="bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 rounded-xl p-4">
            <div className="font-bold text-emerald-700 dark:text-emerald-300 mb-1">{t('lobby.botsOfferTitle')}</div>
            <div className="text-sm text-emerald-600 dark:text-emerald-400 mb-3">{t('lobby.botsOfferText', { count: botsMatchOffer.missing_players })}</div>
            <div className="flex gap-3">
              <button data-testid="accept-bots-match" onClick={onAcceptBotsMatch} className="flex-1 py-2 bg-emerald-500 text-white dark:text-slate-900 font-black rounded-lg">{t('lobby.startWithBots')}</button>
              <button onClick={onDeclineBotsMatch} className="flex-1 py-2 bg-slate-400 dark:bg-slate-500 text-white dark:text-slate-900 font-bold rounded-lg">{t('lobby.keepWaiting')}</button>
            </div>
          </div>
        )}

        {lobbyState && lobbyState.waiting_games.length > 0 && (
          <div className="w-full">
            <h3 className="text-sm font-black text-slate-400 dark:text-slate-400 uppercase tracking-wider mb-3">{t('lobby.waitingGames')}</h3>
            <div className="flex flex-col gap-2">
              {lobbyState.waiting_games.map(game => (
                <div key={game.id} className="flex items-center justify-between bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-3 shadow-sm">
                  <div>
                    <div className="font-bold text-sm text-slate-800 dark:text-slate-100">{game.creator_name}</div>
                    <div className="text-xs text-slate-400 dark:text-slate-400">{game.player_count} / 4 {t('game.players')}</div>
                  </div>
                  <button
                    onClick={() => { if (playerName.trim()) onJoinGame(game.id, preferredColor); }}
                    disabled={!isConnected || !playerName.trim()}
                    className="px-4 py-2 bg-indigo-500 text-white dark:text-slate-900 font-bold text-xs rounded-lg hover:bg-indigo-600 dark:hover:bg-indigo-400 transition-all disabled:opacity-50"
                  >
                    {t('lobby.join')}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="w-full bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-4">
        <h3 className="text-sm font-black text-slate-600 dark:text-slate-300 mb-2">{t('instructions.title')}</h3>
        <ul className="space-y-1 text-xs text-slate-500 dark:text-slate-400 list-disc pl-4">
          {(t('instructions.rules', { returnObjects: true }) as string[]).map((rule, idx) => (
            <li key={idx}>{rule}</li>
          ))}
        </ul>
        <button
          data-testid="lobby-show-rules"
          onClick={onShowRules}
          className="mt-3 w-full py-2 bg-indigo-500 text-white dark:text-slate-900 font-bold text-sm rounded-lg hover:bg-indigo-600 dark:hover:bg-indigo-400 transition-all"
        >
          {t('rules.showRules')}
        </button>
      </div>

      <LanguageSwitcher />

      <div className="flex gap-4 items-center">
        <button
          data-testid="lobby-show-user-agreement"
          onClick={onShowUserAgreement}
          className="text-xs text-slate-500 dark:text-slate-400 underline hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
        >
          {t('userAgreement.title')}
        </button>
        {isLoggedIn && (
          <button onClick={onLogout} className="text-xs text-rose-400 underline opacity-60 hover:opacity-100 transition-opacity">
            {t('account.logout')}
          </button>
        )}
      </div>

      <div className="h-8" />

      {showModeModal && (
        <GameModeModal
          onQuickStart={() => {
            setShowModeModal(false);
            onQuickStart(preferredColor);
          }}
          onWithFriends={() => {
            setShowModeModal(false);
            onCreateGame(true, preferredColor);
          }}
          onFindPlayers={() => {
            setShowModeModal(false);
            onCreateGame(false, preferredColor);
          }}
          onClose={() => setShowModeModal(false)}
        />
      )}
    </div>
  );
}
